import { logError } from 'setup/logger/logError';
import { frontendVersionWithBuildNumber } from 'shared/version';
import { deleteAllCaches, unregisterServiceWorkers } from './clearServiceWorkers';
import { getServiceWorkerRegistrationState } from './diagnostics/getServiceWorkerRegistrationState';

const REPAIR_GUARD_KEY_PREFIX = 'service-worker-repaired:';

/**
 * One automatic repair per session and build: a repair that does not help must
 * not loop. `sessionStorage`, not `LocalStorageManager`: the guard lives as
 * long as the tab's session, a new session may try again.
 */
function claimRepairOfThisSession(): boolean {
  const guardKey = `${REPAIR_GUARD_KEY_PREFIX}${frontendVersionWithBuildNumber}`;
  try {
    if (sessionStorage.getItem(guardKey)) {
      return false;
    }
    sessionStorage.setItem(guardKey, new Date().toISOString());
    return true;
  } catch {
    // Without the guard a failing repair could reload for ever.
    return false;
  }
}

/**
 * The last resort when the worker cannot be brought to the page's version:
 * unregisters every worker and deletes the caches, then reloads, so the page
 * registers the deployed worker from scratch (as on a first visit). The
 * stored data (IndexedDB) stays. Never rejects. Runs at most once per session
 * and build; resolves `false`, without touching anything, when it already ran
 * or cannot be guarded. `trigger` tells in the logs what led to it.
 */
export async function repairServiceWorker(trigger: string): Promise<boolean> {
  if (!claimRepairOfThisSession()) {
    return false;
  }
  logError('Service worker is repaired: unregistered and reloaded', {
    trigger,
    pageVersion: frontendVersionWithBuildNumber,
    registration: await getServiceWorkerRegistrationState().catch(() => undefined),
  });
  // Both run to the end before the reload, also when one fails: a reload must not cut the other short.
  const [unregistering, deletingCaches] = await Promise.allSettled([unregisterServiceWorkers(), deleteAllCaches()]);
  if (unregistering.status === 'rejected' || deletingCaches.status === 'rejected') {
    // The reload below still runs: the page must not stay blocked on a repair that half worked.
    logError('Service worker repair did not finish', {
      trigger,
      unregisterError: unregistering.status === 'rejected' ? unregistering.reason : undefined,
      deleteCachesError: deletingCaches.status === 'rejected' ? deletingCaches.reason : undefined,
    });
  }
  window.location.reload();
  return true;
}
